import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, TrendingUp, Calendar, ChevronRight, Star, DollarSign, Target } from "lucide-react";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import axios from "axios";
import SEO from "../components/SEO";
import Navbar, { AuthModal } from "../components/Navbar";

const MasterclassPage = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const res = await axios.get(`${API}/masterclass`);
        setEntries(res.data);
      } catch (error) {
        console.error("Failed to fetch masterclass entries:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEntries();
  }, []);

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return { bg: "bg-yellow-500", text: "text-black", icon: "🥇" };
    if (rank === 2) return { bg: "bg-zinc-300", text: "text-black", icon: "🥈" };
    if (rank === 3) return { bg: "bg-amber-600", text: "text-white", icon: "🥉" };
    return { bg: "bg-hsl-surface", text: "text-white", icon: `#${rank}` };
  };

  return (
    <div className="min-h-screen bg-hsl-bg" data-testid="masterclass-page">
      <Navbar 
        onLoginClick={() => { setAuthMode("login"); setAuthModalOpen(true); }}
        onRegisterClick={() => { setAuthMode("register"); setAuthModalOpen(true); }}
      />
      
      <SEO 
        title="HSL Masterclass - Top 20 Profit Days | HSL.bet"
        description="See our top 20 highest profit days. Real results from real picks - the best daily performances from Hammer Sports Logic."
        keywords="sports betting profit, best betting days, HSL masterclass, winning picks"
      />

      <main className="pt-20 pb-16">
        {/* Hero Section */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-4 py-2 mb-6">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 font-semibold uppercase tracking-wide">Top 20 Profit Days</span>
              </div>
              
              <h1 className="font-heading text-4xl md:text-6xl font-black uppercase text-white mb-4">
                HSL <span className="text-gradient">Masterclass</span>
              </h1>
              
              <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
                Our highest-profit days documented. These are the moments that define sharp sports analysis.
              </p>
            </motion.div>

            {/* Stats Summary */}
            {entries.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
              >
                <div className="bg-hsl-surface/50 border border-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{entries.length}</p>
                  <p className="text-zinc-500 text-sm uppercase">Elite Days</p>
                </div>
                <div className="bg-hsl-surface/50 border border-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">
                    +{entries.reduce((sum, e) => sum + (e.units_profit || 0), 0).toFixed(1)}u
                  </p>
                  <p className="text-zinc-500 text-sm uppercase">Total Units</p>
                </div>
                <div className="bg-hsl-surface/50 border border-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-400">
                    +${entries.reduce((sum, e) => sum + (e.dollar_profit || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-zinc-500 text-sm uppercase">Total Profit</p>
                </div>
                <div className="bg-hsl-surface/50 border border-white/10 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-hsl-primary">
                    +{entries.length > 0 ? (entries.reduce((sum, e) => sum + (e.units_profit || 0), 0) / entries.length).toFixed(1) : 0}u
                  </p>
                  <p className="text-zinc-500 text-sm uppercase">Avg/Day</p>
                </div>
              </motion.div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-hsl-primary"></div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && entries.length === 0 && (
              <div className="text-center py-20">
                <Trophy className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Coming Soon</h3>
                <p className="text-zinc-400">Our top profit days will be showcased here.</p>
              </div>
            )}

            {/* Masterclass Entries Grid */}
            {!isLoading && entries.length > 0 && (
              <div className="space-y-6">
                {entries.map((entry, index) => {
                  const badge = getRankBadge(entry.rank);
                  return (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`bg-hsl-surface border rounded-2xl overflow-hidden hover:border-hsl-primary/50 transition-all ${
                        entry.rank <= 3 ? 'border-yellow-500/30' : 'border-white/10'
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row">
                        {/* Rank Badge */}
                        <div className={`lg:w-24 flex lg:flex-col items-center justify-center p-4 ${badge.bg}`}>
                          <span className="text-3xl lg:text-4xl font-black">
                            {entry.rank <= 3 ? badge.icon : (
                              <span className={badge.text}>#{entry.rank}</span>
                            )}
                          </span>
                        </div>

                        {/* Image */}
                        {entry.image_url && (
                          <div className="lg:w-72 h-48 lg:h-auto flex-shrink-0">
                            <img 
                              src={entry.image_url} 
                              alt={entry.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}

                        {/* Content */}
                        <div className="flex-1 p-6">
                          <div className="flex flex-wrap items-center gap-3 mb-3">
                            <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                              <Calendar className="w-4 h-4" />
                              {formatDate(entry.date)}
                            </span>
                            <span className="bg-green-500/20 text-green-400 text-sm font-bold px-3 py-1 rounded-full">
                              {entry.picks_summary}
                            </span>
                          </div>

                          <h3 className="font-heading text-2xl font-bold text-white mb-3">
                            {entry.title}
                          </h3>

                          <p className="text-zinc-400 mb-4 line-clamp-2">
                            {entry.analysis}
                          </p>

                          {/* Profit Stats */}
                          <div className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-green-400" />
                              <span className="text-green-400 font-mono font-bold text-xl">
                                +{entry.units_profit}u
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-5 h-5 text-green-400" />
                              <span className="text-green-400 font-mono font-bold text-xl">
                                +${entry.dollar_profit?.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* View Button on larger screens */}
                        <div className="hidden lg:flex items-center px-6">
                          <Button
                            onClick={() => setSelectedEntry(entry)}
                            variant="outline"
                            className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                          >
                            Details <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="mt-16 text-center"
            >
              <div className="bg-gradient-to-br from-hsl-surface to-hsl-surface/50 border border-white/10 rounded-2xl p-8 md:p-12">
                <h2 className="font-heading text-3xl font-bold text-white uppercase mb-4">
                  Want Days Like These?
                </h2>
                <p className="text-zinc-400 mb-6 max-w-xl mx-auto">
                  Join HSL and get access to every pick we make. Start with a free 7-day trial.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/pricing">
                    <Button className="btn-primary-glow px-8 py-6 text-lg">
                      Start Free Trial
                    </Button>
                  </Link>
                  <Link to="/track-record">
                    <Button variant="outline" className="bg-white/5 border-white/10 text-white hover:bg-white/10 px-8 py-6 text-lg">
                      View Full Results
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Detail Modal */}
        {selectedEntry && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setSelectedEntry(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-hsl-surface border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {selectedEntry.image_url && (
                <img 
                  src={selectedEntry.image_url} 
                  alt={selectedEntry.title}
                  className="w-full h-64 object-cover rounded-t-2xl"
                />
              )}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className={`${getRankBadge(selectedEntry.rank).bg} ${getRankBadge(selectedEntry.rank).text} font-bold px-3 py-1 rounded-lg`}>
                    #{selectedEntry.rank}
                  </span>
                  <span className="text-zinc-400">{formatDate(selectedEntry.date)}</span>
                </div>
                
                <h2 className="font-heading text-3xl font-bold text-white mb-4">
                  {selectedEntry.title}
                </h2>
                
                <div className="flex gap-6 mb-6">
                  <div>
                    <p className="text-zinc-500 text-sm">Units Profit</p>
                    <p className="text-green-400 font-mono font-bold text-2xl">+{selectedEntry.units_profit}u</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Dollar Profit</p>
                    <p className="text-green-400 font-mono font-bold text-2xl">+${selectedEntry.dollar_profit?.toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-zinc-500 text-sm mb-2">Picks Summary</p>
                  <p className="text-white bg-hsl-bg rounded-lg px-4 py-2">{selectedEntry.picks_summary}</p>
                </div>
                
                <div>
                  <p className="text-zinc-500 text-sm mb-2">Analysis</p>
                  <p className="text-zinc-300 whitespace-pre-wrap">{selectedEntry.analysis}</p>
                </div>
                
                <Button
                  onClick={() => setSelectedEntry(null)}
                  className="w-full mt-6 bg-white/10 hover:bg-white/20"
                >
                  Close
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </main>

      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        mode={authMode}
        setMode={setAuthMode}
      />
    </div>
  );
};

export default MasterclassPage;
